import { useCallback, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { RouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import includes from 'lodash/includes';
import { useYieldCallback } from '@zillow/yield-callback';

// Components
import GalleryButtonGroupV2 from 'app/shared/modules/hdp/galleryV2/GalleryButtonGroupV2';
import PhotoGalleryPopupModal from 'app/shared/modules/photo-gallery/PhotoGalleryPopupModal';
import GalleryView from './GalleryView';
import MapView from './MapView';
import StreetViewComponent from './StreetViewComponent';

// Misc / Utils
import controller from './controller';
import queryUtils from 'app/shared/utils/queryUtils';
import { emitTrackHdpPhotoGalleryOpenEvent } from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import { HdpPhotoGalleryWrapper } from './HdpPhotoGallery.styles';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { noop } from 'lodash';
import type InstantTourForm from 'app/shared/models/InstantTourForm';
import type { RootReduxState } from 'app/shared/flux/reducers';

interface HdpPhotoGalleryV2Props {
  listing: ListingDetails;
  isMobile: boolean;
  excludeLargePhotos: boolean;
  screenWidth: string;
  instantTourForm: InstantTourForm | null;
  photoIndex: number;
  googleMapsKey: string | undefined;
  onClickContact: () => void;
  onClickOpenFdp: () => void;
}

const HdpPhotoGalleryV2 = ({
  listing,
  isMobile,
  excludeLargePhotos,
  screenWidth,
  instantTourForm,
  photoIndex,
  googleMapsKey,
  onClickContact = noop,
  onClickOpenFdp = noop,
}: HdpPhotoGalleryV2Props) => {
  const dispatch = useDispatch();
  const [visible, setVisible] = useState('gallery');
  const [showPhotoGalleryFull, setShowPhotoGalleryFull] = useState(false);
  const [statePhotoIdx, setStatePhotoIdx] = useState(photoIndex);
  const [photoGalleryFullEventFired, setPhotoGalleryFullEventFired] = useState(false);
  const photos = useMemo(
    () => controller.getPhotos(listing, excludeLargePhotos, googleMapsKey),
    [excludeLargePhotos, googleMapsKey, listing],
  );
  const [show3dToursIframe, setShow3dToursIframe] = useState(false);
  const [isLoadingAllPhotos, setIsLoadingAllPhotos] = useState(true);
  const { active: isListingActive, isAllowedToRetain } = listing;
  /**
   * Data should be hidden per MLS if the listing is:
   * -- active: false
   * -- isAllowedToRetain: false
   */
  const shouldBeHiddenIfDataCannotBeRetained = isListingActive === false && isAllowedToRetain === false;

  const handleOpenPhotoGalleryFull = useCallback(() => {
    if (!photoGalleryFullEventFired) {
      emitTrackHdpPhotoGalleryOpenEvent();
      setPhotoGalleryFullEventFired(true);
    }
    setShowPhotoGalleryFull(true);
  }, [photoGalleryFullEventFired]);

  const yieldHandleSelectOption = useYieldCallback((clickedBtn) => {
    if (visible === 'gallery' && clickedBtn === 'gallery') {
      handleOpenPhotoGalleryFull();
      return;
    }

    setVisible(clickedBtn);
  });

  const loadAllPhotos = useCallback(() => {
    if (!isLoadingAllPhotos) {
      setIsLoadingAllPhotos(true);
    }
  }, [isLoadingAllPhotos, setIsLoadingAllPhotos]);

  const handleOpen3dTours = useYieldCallback(() => {
    setShow3dToursIframe(true);
  });

  // change due to click left/right in main photo gallery
  const handlePhotoCarouselChange = useYieldCallback((i) => {
    loadAllPhotos();
    setStatePhotoIdx(i);
  });

  const isMediumOrSmallerScreenSize = !includes(['lg', 'xl'], screenWidth);
  const { details } = listing;
  const showContactInfo = details && details.isMls && isMobile;
  const ignoreKeypress = instantTourForm?.isOpen || showPhotoGalleryFull || show3dToursIframe;

  const onPhotoGalleryPopupModalClose = useCallback(() => {
    setShowPhotoGalleryFull(false);
    setShow3dToursIframe(false);
  }, [setShowPhotoGalleryFull, setShow3dToursIframe]);

  if (!listing) {
    return null;
  }

  return (
    <HdpPhotoGalleryWrapper>
      {visible === 'gallery' && (
        <GalleryView
          listing={listing}
          photos={photos}
          isLoadingAllPhotos={isLoadingAllPhotos}
          statePhotoIdx={statePhotoIdx}
          isMediumOrSmallerScreenSize={isMediumOrSmallerScreenSize}
          ignoreKeypress={ignoreKeypress}
          shouldBeHiddenIfDataCannotBeRetained={shouldBeHiddenIfDataCannotBeRetained}
          showContactInfo={showContactInfo}
          onPhotoCarouselChange={handlePhotoCarouselChange}
          onOpenPhotoGalleryFull={handleOpenPhotoGalleryFull}
          onOpen3dTours={handleOpen3dTours}
          onClickContact={onClickContact}
        />
      )}
      {visible === 'map' && <MapView listing={listing} />}
      {visible === 'street' && <StreetViewComponent listing={listing} />}
      {shouldBeHiddenIfDataCannotBeRetained ? null : (
        <GalleryButtonGroupV2
          isMobile={isMobile}
          visible={visible}
          numPhotos={photos.length}
          onYieldSelectOption={yieldHandleSelectOption}
        />
      )}
      {(showPhotoGalleryFull || show3dToursIframe) && (
        <PhotoGalleryPopupModal
          onClose={onPhotoGalleryPopupModalClose}
          listing={listing}
          photos={photos}
          onClickContact={onClickContact}
          onClickOpenFdp={onClickOpenFdp}
          show3dToursIframe={show3dToursIframe}
        />
      )}
    </HdpPhotoGalleryWrapper>
  );
};

const mapStateToProps = (state: RootReduxState, ownProps: RouteComponentProps) => {
  const queryParams = queryUtils.parse(ownProps.location.search) as { image?: string };
  return {
    isMobile: state.app.device.screenWidth === 'sm' || state.app.device.screenWidth === 'xs',
    // use app.device.deviceType b.c isMobile/screenWidths is not immediately ready to use after SSR.
    excludeLargePhotos: state.app.device.deviceType !== 'desktop',
    photoIndex: Number(queryParams.image) || 0,
    screenWidth: state.app.device.screenWidth,
    instantTourForm: state.currentListingDetails.instantTourForm,
    googleMapsKey: state.app.googleMaps?.key,
  };
};

export default withRouter(connect(mapStateToProps)(HdpPhotoGalleryV2));
